import styled from "styled-components";

export const Page = styled.div`
  --ink: #14243a;
  --muted: #4a5b72;
  --line: #d6dee9;
  --line2: #e4eaf2;
  --blue: #1d5ba8;
  --blue-d: #13427d;
  --blue-l: #e3edf9;
  --amber-bg: #fff9ee;
  --warn: #c77700;
  --ina: #b4510e;
  --green: #2e8b6e;
  --green-d: #245c49;
  --green-l: #eef7f2;
  --red: #a12d2d;

  box-sizing: border-box;
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: var(--ink);
  background: #eef2f7;
  font-size: 14px;

  button,
  select,
  input {
    font: inherit;
    box-sizing: border-box;
  }

  button:focus-visible,
  select:focus-visible,
  input:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 2px;
  }
`;

export const Scroll = styled.div`
  box-sizing: border-box;
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: 24px 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const Badge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: var(--blue);
  border-radius: 4px;
  padding: 2px 8px;
`;

export const Lead = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--muted);
  max-width: 100ch;
`;

export const TopBand = styled.div`
  position: sticky;
  top: 0;
  z-index: 4;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  min-width: 0;
  background: #eef2f7;
  padding-bottom: 4px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Notes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

export const Note = styled.div<{ $tone: "warn" | "info" | "param" }>`
  max-width: none;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  border: 1px solid;
  border-left-width: 4px;
  background: ${({ $tone }) =>
    $tone === "warn"
      ? "#fff6e6"
      : $tone === "info"
      ? "#f4f8fd"
      : "var(--green-l)"};
  border-color: ${({ $tone }) =>
    $tone === "warn" ? "#f0c27a" : $tone === "info" ? "#c5d6ec" : "#b9dccb"};
  border-left-color: ${({ $tone }) =>
    $tone === "warn"
      ? "var(--warn)"
      : $tone === "info"
      ? "var(--blue)"
      : "var(--green)"};
  color: ${({ $tone }) =>
    $tone === "warn" ? "#5b3a00" : $tone === "info" ? "#2d3d52" : "#1f3d31"};
`;

export const TableWrap = styled.div`
  box-sizing: border-box;
  flex: 0 0 auto;
  align-self: stretch;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: auto;
  min-height: min-content;
  overflow-x: auto;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
`;

export const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
  min-width: 3220px;
  font-size: 13px;

  th {
    background: #f4f7fb;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: var(--muted);
    padding: 9px 6px;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }

  th.r {
    text-align: right;
  }

  th.grp {
    background: #fff1b8;
    color: #5a4500;
    text-align: center;
    border-left: 1px solid #e9d27a;
    border-right: 1px solid #e9d27a;
  }

  th.txh,
  td.tx {
    width: 200px;
    min-width: 200px;
  }

  th.txh {
    background: #fff8d9;
    color: #5a4500;
    font-weight: 500;
    white-space: normal;
    line-height: 1.25;
  }

  td.tx {
    background: #fff;
    color: var(--ink);
    font-weight: 400;
  }

  td {
    padding: 4px 5px;
    border-bottom: 1px solid var(--line2);
    vertical-align: middle;
    background: #fff;
  }

  .s0 {
    position: sticky;
    left: 0;
    z-index: 1;
    width: 36px;
    background: #fff;
  }

  .s1 {
    position: sticky;
    left: 36px;
    z-index: 1;
    width: 48px;
    text-align: center;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
    background: #fff;
  }

  th.s0,
  th.s1 {
    z-index: 2;
    background: #f4f7fb;
  }

  tr.dragging td {
    opacity: 0.4;
  }

  tr.drop-before td {
    box-shadow: inset 0 3px 0 var(--blue);
  }

  tr.drop-after td {
    box-shadow: inset 0 -3px 0 var(--blue);
  }

  tfoot td {
    border-bottom: 0;
    padding: 10px 5px;
    font-weight: 700;
    background: #fff;
  }
`;

export const Field = styled.input`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #bcc8d8;
  border-radius: 4px;
  background: #fff;
  font-size: 13px;
  color: var(--ink);

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
    border-color: var(--blue);
  }

  &:disabled {
    background: #eef1f5;
    color: #7d8a9c;
  }

  &.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &.warn {
    border-color: var(--warn);
    background: var(--amber-bg);
  }

  &.own {
    border-color: var(--blue);
    color: var(--blue-d);
    font-weight: 600;
  }

  &.lnk {
    border-left: 3px solid var(--green);
  }
`;

export const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #bcc8d8;
  border-radius: 4px;
  background: #fff;
  font-size: 13px;
  color: var(--ink);

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
    border-color: var(--blue);
  }

  &:disabled {
    background: #eef1f5;
    color: #7d8a9c;
  }

  &.warn {
    border-color: var(--warn);
    background: var(--amber-bg);
  }
`;

export const Combo = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(96px, 1fr);
  width: 100%;
  min-width: 180px;

  && select,
  && input {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  && select {
    border-radius: 4px 0 0 4px;
    border-right: 0;
    padding: 0 4px 0 8px;
  }

  && input {
    border-radius: 0 4px 4px 0;
  }
`;

export const Inactive = styled.span`
  display: block;
  text-align: center;
  font-size: 12px;
  color: var(--ina);
  white-space: nowrap;
`;

export const Linked = styled.span`
  display: block;
  font-size: 11px;
  color: var(--green-d);
  margin-top: 3px;
  white-space: nowrap;
`;

export const Handle = styled.button`
  width: 26px;
  height: 32px;
  border: 0;
  background: none;
  color: #6b7889;
  border-radius: 4px;
  display: grid;
  place-items: center;
  cursor: grab;

  &:hover {
    background: var(--blue-l);
    color: var(--blue-d);
  }
`;

export const Remove = styled.button`
  width: 30px;
  height: 30px;
  border: 0;
  background: none;
  color: var(--red);
  border-radius: 4px;
  display: grid;
  place-items: center;

  &:hover {
    background: #fbeaea;
  }
`;

export const AddLine = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px dashed var(--blue);
  background: var(--blue-l);
  color: var(--blue-d);
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px;
`;

export const Totals = styled.section`
  box-sizing: border-box;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 14px 16px;
  width: 100%;
  max-width: none;
  min-width: 0;

  h3 {
    margin: 0 0 10px;
    font-size: 15px;
    font-weight: 600;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    min-width: 0;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid var(--line2);
    text-align: right;
  }

  th:first-child {
    text-align: left;
  }

  thead th {
    font-size: 12px;
    color: var(--muted);
    background: #f4f7fb;
  }

  tr.g th,
  tr.g td {
    background: var(--blue-l);
    color: var(--blue-d);
    font-weight: 700;
    border-bottom: 0;
  }
`;

export const Footer = styled.footer`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: #fff;
  border-top: 1px solid var(--line);
  padding: 12px 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  font-size: 13px;
  color: var(--muted);

  strong {
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
`;

export const Save = styled.button`
  margin-left: auto;
  height: 40px;
  padding: 0 18px;
  border: 0;
  border-radius: 4px;
  background: var(--blue);
  color: #fff;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
  }
`;

export const Sub = styled.span`
  font-weight: 400;
  color: var(--muted);
  margin-left: 6px;
`;
